/**
 * El índice de conducción del día y el desglose de alertas.
 *
 * ── Por qué esto vive en un módulo propio ───────────────────────────────────
 * Lo usan dos pantallas —el panel de Conducción en monitoreo y el historial en
 * reportes— y un número que califica a una persona no puede dar distinto según
 * dónde lo mires. La regla está escrita una vez, en un solo lugar, y probada.
 *
 * ── Por qué es PROVISIONAL, y hay que decirlo ───────────────────────────────
 * Esto NO es comparable entre conductores, y es importante entender por qué
 * antes de usarlo para decidir algo sobre alguien: cuenta hechos sin dividir
 * entre exposición. Quien manejó 400 km siempre va a acumular más salidas de
 * carril que quien manejó 40, sin ser peor conductor — al contrario. Un índice
 * justo se normaliza por kilómetros u horas al volante, y ninguno de los dos
 * está disponible todavía (salen del flujo de posiciones, que el parte no lee).
 *
 * Mientras tanto sirve para UNA cosa legítima: comparar a la misma unidad
 * consigo misma de un día a otro. Para eso la pantalla lo marca como
 * provisional y enseña la fórmula. Para calificar personas, todavía no.
 *
 * ── Por qué raíz cuadrada y no multiplicación directa ───────────────────────
 * Treinta salidas de carril no son treinta veces peor que una: la primera dice
 * «pasó algo», la trigésima dice «es un patrón», y entre la 29 y la 30 no hay
 * información nueva. Con peso lineal, un día largo en carretera daba cero y el
 * número dejaba de distinguir un mal día de un día muy malo.
 */

/** Cuánto descuenta cada conducta, por la raíz de las veces que ocurrió. */
export const PESOS: Record<string, number> = {
  // Cabina: el conductor no está en condiciones de manejar.
  dms_microsleep: 25,
  dms_fatigue: 15,
  dms_phone: 12,
  dms_camera_covered: 10, // tapar la cámara es deliberado, por eso pesa
  dms_distraction: 8,
  // Carretera: riesgo real, pero muchas veces bien resuelto por el conductor.
  adas_forward_collision: 10,
  adas_headway: 4,
  adas_lane_departure: 3,
};

/** Peso de una conducta que no está en el catálogo: se cuenta, no se ignora. */
export const PESO_DESCONOCIDO = 5;

export interface ConductaContada {
  signal: string;
  etiqueta: string;
  veces: number;
}

/**
 * El índice del día, de 0 a 100. 100 es «la cámara no vio nada».
 *
 * Devuelve `null` cuando no hay cámara: un GPS pelado no tiene con qué
 * calificar la conducción, y un 100 ahí sería una afirmación falsa —diría «no
 * hizo nada mal» cuando la verdad es «no hay quién lo haya visto».
 */
export function indiceDelDia(
  conductas: ConductaContada[] | null | undefined,
  { hayCamara = true }: { hayCamara?: boolean } = {},
): number | null {
  if (!hayCamara) return null;
  if (!conductas) return null;

  const descuento = conductas.reduce((suma, c) => {
    const veces = Number(c.veces);
    if (!Number.isFinite(veces) || veces <= 0) return suma;
    const peso = PESOS[c.signal] ?? PESO_DESCONOCIDO;
    return suma + peso * Math.sqrt(veces);
  }, 0);

  return Math.max(0, Math.min(100, Math.round(100 - descuento)));
}

/** El tono con el que se pinta el índice. Tres bandas, sin palabra de veredicto:
 *  «EXCELENTE» sobre un número que no está normalizado sería pasarse. */
export function tonoDelIndice(indice: number | null): "success" | "warning" | "error" | "neutral" {
  if (indice === null) return "neutral";
  if (indice >= 80) return "success";
  if (indice >= 50) return "warning";
  return "error";
}

export interface RenglonDesglose {
  clave: string;
  etiqueta: string;
  veces: number;
  /** Porcentaje del total de hechos del día, redondeado. */
  porcentaje: number;
}

/**
 * El reparto de las alertas del día, de la más frecuente a la menos.
 *
 * Es un porcentaje SOBRE EL TOTAL DE HECHOS, no sobre 100 puntos de riesgo:
 * responde «¿de qué se trató el día?», no «¿qué tan grave fue?». Las dos
 * preguntas son distintas y mezclarlas fue la tentación — una barra al 97%
 * junto a la palabra «fatiga» se lee como «el conductor estuvo dormido el 97%
 * del tiempo», que no es lo que el dato dice.
 */
export function desglose(conductas: ConductaContada[] | null | undefined): RenglonDesglose[] {
  const lista = (conductas ?? []).filter((c) => Number(c.veces) > 0);
  const total = lista.reduce((n, c) => n + Number(c.veces), 0);
  if (!total) return [];

  return lista
    .map((c) => ({
      clave: c.signal,
      etiqueta: c.etiqueta,
      veces: Number(c.veces),
      porcentaje: Math.round((Number(c.veces) / total) * 100),
    }))
    .sort((a, b) => b.veces - a.veces);
}
